import { Field, ObjectType, InputType } from 'type-graphql'
import { Prop } from '@typegoose/typegoose'
import type { MailgunEventTypeEnum } from './MailgunEventTypeEnum'

// --------------------------------------------
// SUB OBJECT TYPES
// --------------------------------------------

@ObjectType()
@InputType('MailgunSignatureInput')
export class MailgunSignature {
  @Field(() => String)
  @Prop({ type: String })
  timestamp: string

  @Field(() => String)
  @Prop({ type: String })
  token: string

  @Field(() => String)
  @Prop({ type: String })
  signature: string
}

@ObjectType()
@InputType('MailgunDeliveryStatusInput')
export class MailgunDeliveryStatus {
  @Field(() => Boolean)
  @Prop({ type: Boolean })
  tls?: boolean

  @Field(() => String)
  @Prop({ type: String })
  mxHost?: string

  @Field(() => Number)
  @Prop({ type: Number })
  attemptNo?: number

  @Field(() => String)
  @Prop({ type: String })
  description?: string

  @Field(() => Number)
  @Prop({ type: Number })
  code?: number

  @Field(() => String)
  @Prop({ type: String })
  message?: string
}

@ObjectType()
@InputType('MailgunGeoInput')
export class MailgunGeo {
  @Field(() => String)
  @Prop({ type: String })
  country?: string

  @Field(() => String)
  @Prop({ type: String })
  region?: string

  @Field(() => String)
  @Prop({ type: String })
  city?: string
}

@ObjectType()
@InputType('MailgunClientInfoInput')
export class MailgunClientInfo {
  @Field(() => String)
  @Prop({ type: String })
  clientType?: string

  @Field(() => String)
  @Prop({ type: String })
  clientName?: string

  @Field(() => String)
  @Prop({ type: String })
  clientOs?: string

  @Field(() => String)
  @Prop({ type: String })
  deviceType?: string
}

@ObjectType()
@InputType('MailgunMessageHeadersInput')
export class MailgunMessageHeaders {
  @Field(() => String)
  @Prop({ type: String })
  to?: string

  @Field(() => String)
  @Prop({ type: String })
  from?: string

  @Field(() => String)
  @Prop({ type: String })
  subject?: string

  @Field(() => String)
  @Prop({ type: String })
  messageId?: string
}

@ObjectType()
@InputType('MailgunMessageInput')
export class MailgunMessage {
  @Field(() => MailgunMessageHeaders)
  @Prop({ type: MailgunMessageHeaders })
  headers?: MailgunMessageHeaders

  @Field(() => Number)
  @Prop({ type: Number })
  size?: number
}

@ObjectType()
@InputType('MailgunStorageInput')
export class MailgunStorage {
  @Field(() => String)
  @Prop({ type: String })
  url?: string

  @Field(() => String)
  @Prop({ type: String })
  key?: string
}

@ObjectType()
@InputType('MailgunFlagsInput')
export class MailgunFlags {
  @Field(() => Boolean, { nullable: true })
  @Prop({ type: Boolean })
  isTestMode?: boolean

  @Field(() => Boolean, { nullable: true })
  @Prop({ type: Boolean })
  isSystemTest?: boolean

  @Field(() => Boolean, { nullable: true })
  @Prop({ type: Boolean })
  isRouted?: boolean

  @Field(() => Boolean, { nullable: true })
  @Prop({ type: Boolean })
  isAuthenticated?: boolean
}

// --------------------------------------------
// MAIN EVENT-DATA TYPE
// --------------------------------------------

@ObjectType()
@InputType('MailgunEventDataInput')
export class MailgunEventData {
  @Field(() => String)
  @Prop({ type: String })
  id: string

  @Field(() => String)
  @Prop({ type: String })
  event: MailgunEventTypeEnum

  @Field(() => Number)
  @Prop({ type: Number })
  timestamp: number

  @Field(() => String)
  @Prop({ type: String })
  recipient: string

  @Field(() => String)
  @Prop({ type: String })
  recipientDomain?: string

  @Field(() => [String])
  @Prop({ type: [String] })
  tags?: string[]

  @Field(() => [String])
  @Prop({ type: [String] })
  campaigns?: string[]

  @Field(() => MailgunDeliveryStatus)
  @Prop({ type: MailgunDeliveryStatus })
  deliveryStatus?: MailgunDeliveryStatus

  @Field(() => MailgunGeo)
  @Prop({ type: MailgunGeo })
  geo?: MailgunGeo

  @Field(() => MailgunClientInfo)
  @Prop({ type: MailgunClientInfo })
  clientInfo?: MailgunClientInfo

  @Field(() => String)
  @Prop({ type: String })
  ip?: string

  @Field(() => String)
  @Prop({ type: String })
  url?: string

  @Field(() => String)
  @Prop({ type: String })
  severity?: string

  @Field(() => String)
  @Prop({ type: String })
  reason?: string

  @Field(() => MailgunMessage)
  @Prop({ type: MailgunMessage })
  message?: MailgunMessage

  @Field(() => MailgunStorage)
  @Prop({ type: MailgunStorage })
  storage?: MailgunStorage

  @Field(() => MailgunFlags, { nullable: true })
  @Prop({ type: MailgunFlags })
  flags?: MailgunFlags
}

// --------------------------------------------
// WRAPPER: WEBHOOK BODY
// --------------------------------------------

@ObjectType()
@InputType('MailgunWebhookInput')
export class MailgunWebhook {
  @Field(() => MailgunSignature)
  @Prop({ type: MailgunSignature })
  signature: MailgunSignature

  @Field(() => MailgunEventData) // 👈 map to 'event-data'
  @Prop({ type: MailgunEventData })
  eventData: MailgunEventData
}
